import * as yaml from 'js-yaml'
import * as path from 'path'
import { DEFAULT_KUBE_CONFIG } from "yam-operator/constants"
import KubernetesOperatorClient from "yam-operator/kube/kube-client"
import { KubernetesEnvironment } from 'yam-operator/types'
import { createLogger, fsExist, fsRead, lookupWildcardKeyObj, splitAndTrim, trimAndLowerAndRemoveSlash } from 'yam-operator/util'
import { IMetadata, KV } from "yam-types"
import { engineSettings } from "./engine"


const log = createLogger('cli')

export async function createKubeClient4Svr(cluster: string, externalKubeConf?: KV): Promise<KubernetesOperatorClient> {
  let rawCtx, kubeConf
  if (externalKubeConf) {
    rawCtx = cluster
    kubeConf = externalKubeConf
  } else {
    const localConf = await checkKubeConfAndContext(cluster)
    rawCtx = localConf.rawCtx
    kubeConf = localConf.kubeConf
  }

  const kubeCtx = lookupWildcardKeyObj(rawCtx, engineSettings.clientConf.contextPatternMapping) || rawCtx
  const normalizedEnv = trimAndLowerAndRemoveSlash(rawCtx)
  const envLocators = normalizedEnv.split('/')
  const env: KubernetesEnvironment = {
    name: normalizedEnv, stack: envLocators[0], envLocators,
    kubeConfPath: kubeConf as unknown as string,
    kubeConfContextName: kubeCtx as string,
    rawClusterName: rawCtx
  }
  const client = await KubernetesOperatorClient.create([env], { app: '', namespace: '' }, false, true)
  client.setCurrentContext(env)
  return client
}

export async function createKubeClient4Cmd(workload?: string, context?: string, dir?: string, enableLog?: boolean)
  : Promise<{ kubeClient: KubernetesOperatorClient, app: string, namespace: string }> {
  // could be "app", "namespace/app", "podName", "namespace/podName"
  let { namespace, app } = splitWorkload(workload)

  // if workload not set and running inside YAM model workingDir, retrieve app.yaml by default
  const detectModelPath = path.join(dir || process.cwd(), 'app.yaml')
  if (!app) {
    if (await fsExist(detectModelPath)) {
      const data = await fsRead(detectModelPath)
      const metadata = ((yaml.load(data.toString()) || {}) as KV)?.['metadata'] as IMetadata
      if (metadata) {
        namespace = metadata.namespace
        app = metadata.app
      }
    }
  }
  if (!namespace) {
    namespace = engineSettings.clientConf.currentNamespace
  }

  if (!namespace || !app) {
    throw new Error(`application or namespace not specified.`)
  }
  const { rawCtx, kubeConf } = await checkKubeConfAndContext(context)
  const name = trimAndLowerAndRemoveSlash(rawCtx)
  const kubeCtx = lookupWildcardKeyObj(name, engineSettings.clientConf.contextPatternMapping) || rawCtx
  const ctxParts = splitAndTrim(rawCtx)
  const env: KubernetesEnvironment = {
    name,
    stack: ctxParts[0],
    envLocators: ctxParts,
    kubeConfPath: kubeConf,
    kubeConfContextName: kubeCtx as string,
    rawClusterName: rawCtx
  }

  // create kube client
  const client = await KubernetesOperatorClient.create([env], { app, namespace }, false, true)
  client.setCurrentContext(env)
  if (!process.env.DEBUG) {
    !enableLog && client.disableLog()
  }
  log.info(`cluster info loaded, operating in cluster: ${rawCtx}`)
  return { kubeClient: client, app, namespace }
}

function splitWorkload(wl?: string) {
  if (!wl) {
    return {}
  }
  const tmp = wl.split('/')
  if (tmp.length === 2) {
    return {
      namespace: tmp[0].trim(),
      app: tmp[1].trim()
    }
  } else {
    return {
      app: wl.trim()
    }
  }
}

export async function checkKubeConfAndContext(context?: string): Promise<{ rawCtx: string, kubeConf: string }> {
  // resolve kube context, from default location or settings, and then convert it if needed
  const kubeConf =
    (lookupWildcardKeyObj(trimAndLowerAndRemoveSlash(context || ''), engineSettings.clientConf?.kubeConfigFilePatternMapping)
      || engineSettings.clientConf.kubeConfig || DEFAULT_KUBE_CONFIG()) as string
  if (kubeConf.indexOf('kind: Config') !== -1) {
    log.info(`kubeConfig is provided with raw string data`)
    return { rawCtx: (((yaml.load(kubeConf) || {}) as KV)['current-context'] as string), kubeConf }
  }
  if (!await fsExist(kubeConf)) {
    throw new Error(`${kubeConf} doesn't exist`)
  }
  let rawCtx = context
  if (!rawCtx) {
    // context not specified, use default kubeConfig and current context
    const data = await fsRead(kubeConf)
    const defaultCtx = engineSettings.clientConf.currentContext || (((yaml.load(data.toString()) || {}) as KV)['current-context'] as string)
    if (!defaultCtx) {
      throw new Error(`kube context is not set with valid value`)
    }
    rawCtx = defaultCtx
  }
  return { rawCtx, kubeConf }
}